/* eslint-disable react/jsx-props-no-spreading */
import React, { useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import i18n from 'i18next';

import NotificationsIcon from '@mui/icons-material/Notifications';
import ReportProblemOutlined from '@mui/icons-material/ReportProblemOutlined';

import { Avatar } from 'uikit/avatars';
import { Button } from 'uikit/buttons';
import { createIconComponent } from 'uikit/icons';
import { Meta } from 'uikit/meta';
import { Typo } from 'uikit/content';
import Menu from './menu';
import MenuItem from './menuItem';

import styles from '../styles/uikitMenusUserMenu.module.scss';

const UserMenu = ({
  buttonAriaLabel = '',
  buttonClassName = '',
  buttonColor = 'primary',
  hasNotificationsControl = false,
  menuItemGroups = [],
  notificationsMessage = i18n.t('enable_notifications', 'Enable Notifications'),
  onNotificationsClick = () => {},
  shouldShowGamification = false,
  shouldShowGold = false,
  user = {},
}) => {
  const menuRef = useRef();

  return (
    <div ref={menuRef} className={styles.element}>
      <Menu
        component={
          <Button
            ariaLabel={
              buttonAriaLabel ||
              i18n.t('main-menu', {
                user: user.name,
                defaultValue: `${user.name} — main menu`,
              })
            }
            className={classnames(styles.button, buttonClassName)}
            color={buttonColor}
            size="small"
            variant="ghost"
          >
            <UserAvatar user={user} />
          </Button>
        }
        className={styles.menu}
        shouldCloseOnTab={false}
        shouldSetFocus={false}
        variant="userMenu"
      >
        {hasNotificationsControl && (
          <li className={styles.notificationsControl}>
            <Button
              className={styles.notificationsButton}
              icon={<NotificationsIcon />}
              onClick={onNotificationsClick}
              size="small"
              text={notificationsMessage}
              variant="ghost"
            />
          </li>
        )}
        <UserInfo shouldShowGamification={shouldShowGamification} user={user} />
        {shouldShowGold && <UserGold gold={user.brandingSettings?.gold} />}
        {menuItemGroups.map((group, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={index}>
            <ul className={styles.menuItems}>
              {group.map((item) => (
                <UserMenuItem menuItem={item} key={`${item.url}-userMenu`} />
              ))}
            </ul>
          </li>
        ))}
      </Menu>
    </div>
  );
};

const UserAvatar = ({ user }) => {
  const { avatar, name } = user;
  return avatar ? (
    <Avatar ariaHidden avatar={avatar} className={styles.avatar} name={name} />
  ) : (
    createIconComponent({ ariaHidden: true, icon: 'defaultAvatar', size: 30 })
  );
};

const UserMenuItem = ({ iconSize = 18, menuItem = null }) => {
  const { pathname } = useLocation();
  if (!menuItem) return null;
  const { icon = null, target = '', text = '', type = '', url = '' } = menuItem;
  const isLinkCurrentLocation = pathname.includes(url);
  const MenuLink = type === 'internal' ? Link : 'a';

  const linkProps = {
    className: styles.menuItemText,
    [type === 'internal' ? 'to' : 'href']: url,
    ...(type === 'internal'
      ? { 'aria-current': isLinkCurrentLocation ? 'page' : '' }
      : { target: target || null }),
  };

  const content = (
    <>
      {icon && (
        <div>
          {createIconComponent({
            icon,
            size: iconSize,
          })}
        </div>
      )}
      <Typo element="span" variant="s02">
        {text}
      </Typo>
    </>
  );

  return (
    <MenuItem className={styles.menuItem}>
      <MenuLink {...linkProps}>{content}</MenuLink>
    </MenuItem>
  );
};

const UserInfo = ({ shouldShowGamification = false, user }) => {
  // eslint-disable-next-line react/prop-types
  const { email, name, gamification } = user;
  const gamificationMeta = [];

  if (shouldShowGamification) {
    if (gamification?.totalReputationPoints) {
      gamificationMeta.push({
        icon: 'reputation',
        iconTooltipText: i18n.t('reputation', 'Reputation'),
        text: gamification.totalReputationPoints.toString(),
      });
    }

    if (gamification?.credentialsCount) {
      gamificationMeta.push({
        icon: 'credential',
        iconTooltipText: i18n.t('credentials', 'Credentials'),
        text: gamification.credentialsCount.toString(),
      });
    }

    if (gamification?.trophiesCount) {
      gamificationMeta.push({
        icon: 'trophy',
        iconTooltipText: i18n.t('trophies', 'Trophies'),
        text: gamification.trophiesCount.toString(),
      });
    }
  }

  return (
    <li className={styles.userInfo}>
      <div className={styles.userDetails}>
        <UserAvatar user={user} />
        <div className={styles.userDetailsText}>
          <Typo element="div" variant="d01" weight="medium">
            {name}
          </Typo>
          {email && (
            <Typo element="span" variant="s01" className={styles.userEmail}>
              {email}
            </Typo>
          )}
        </div>
      </div>
      {!!gamificationMeta.length && (
        <Meta>
          {gamificationMeta.map((meta) => (
            <Meta.Item
              key={meta.icon}
              icon={meta.icon}
              iconTooltipText={meta.iconTooltipText}
              text={meta.text}
            />
          ))}
        </Meta>
      )}
    </li>
  );
};

const UserGold = ({ gold = null }) => {
  if (!gold) return null;
  const {
    reached_annual_gold_limit: reachedAnnualGoldLimit,
    redeem_link: redeemLink,
    total,
    view_history_link: viewHistoryLink,
  } = gold;

  const shouldShowContent = !Number.isNaN(total) || reachedAnnualGoldLimit;

  return (
    <li className={styles.gold} data-testid="user-gold-section">
      {shouldShowContent && (
        <div className={styles.goldContent}>
          {!Number.isNaN(total) && (
            <div className={styles.goldCount}>
              {createIconComponent({ ariaHidden: true, icon: 'gold', size: 24 })}
              <Typo element="span" variant="d01">
                {i18n.t('gold-count', { count: total, defaultValue: `${total} Gold` })}
              </Typo>
            </div>
          )}
          {reachedAnnualGoldLimit && (
            <div className={styles.goldLimitWarning}>
              <ReportProblemOutlined sx={{ height: 12, width: 12 }} />
              <Typo
                color="secondary"
                element="span"
                variant="s01"
                className={styles.goldLimitWarningText}
              >
                {i18n.t('gold-limit-reached', "You've reached the annual gold limit!")}
              </Typo>
            </div>
          )}
        </div>
      )}
      <ul className={styles.menuItems}>
        {redeemLink && <UserMenuItem menuItem={redeemLink} key="redeemLink" />}
        {viewHistoryLink && <UserMenuItem menuItem={viewHistoryLink} key="viewHistoryLink" />}
      </ul>
    </li>
  );
};

const userPropShape = {
  avatar: PropTypes.string,
  email: PropTypes.string,
  name: PropTypes.string,
};

const menuItemPropShape = {
  url: PropTypes.string.isRequired,
  type: PropTypes.string,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  text: PropTypes.string.isRequired,
  target: PropTypes.string,
};

const goldPropShape = {
  reached_annual_gold_limit: PropTypes.bool,
  redeem_link: PropTypes.shape(menuItemPropShape),
  total: PropTypes.number,
  view_history_link: PropTypes.shape(menuItemPropShape),
};

UserMenu.propTypes = {
  buttonAriaLabel: PropTypes.string,
  buttonClassName: PropTypes.string,
  buttonColor: PropTypes.string,
  hasNotificationsControl: PropTypes.bool,
  menuItemGroups: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape(menuItemPropShape))),
  notificationsMessage: PropTypes.string,
  onNotificationsClick: PropTypes.func,
  shouldShowGamification: PropTypes.bool,
  shouldShowGold: PropTypes.bool,
  user: PropTypes.shape({
    ...userPropShape,
    brandingSettings: PropTypes.shape({
      gold: PropTypes.shape(goldPropShape),
    }),
  }),
};

UserMenuItem.propTypes = {
  iconSize: PropTypes.number,
  menuItem: PropTypes.shape(menuItemPropShape),
};

UserAvatar.propTypes = {
  user: PropTypes.shape(userPropShape).isRequired,
};

UserInfo.propTypes = {
  shouldShowGamification: PropTypes.bool,
  user: PropTypes.shape({
    ...userPropShape,
    gamification: PropTypes.shape({
      totalReputationPoints: PropTypes.number,
      credentialsCount: PropTypes.number,
      trophiesCount: PropTypes.number,
    }),
  }).isRequired,
};

UserGold.propTypes = {
  gold: PropTypes.shape(goldPropShape),
};

UserMenu.Avatar = UserAvatar;
UserMenu.Item = UserMenuItem;
UserMenu.Info = UserInfo;
UserMenu.Gold = UserGold;

export default UserMenu;
